/**
 * AgentOsDemoCustomerOnboarding.jsx — sandbox for Customer Onboarding Bot.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Sparkles, Loader2, Copy, CheckCircle, UserPlus } from "lucide-react";
import AgentOsDemoShell from "./AgentOsDemoShell";

const STAGES = [
  { id: "welcome", name: "Welcome (Day 0)" }, { id: "tip_day3", name: "Pro Tip (Day 3)" },
  { id: "review_request", name: "Review Request (Day 14)" }, { id: "upsell", name: "Upsell (Day 30)" },
  { id: "winback", name: "Winback (Day 90+)" },
];
const CHANNELS = [{ id: "email", name: "Email" }, { id: "whatsapp", name: "WhatsApp" }, { id: "sms", name: "SMS" }];
const EXAMPLES = [
  { customer_name: "Lucia", product_purchased: "Sandalwood hodinky Aurora", stage: "welcome", channel: "email", brand_name: "WoodTime" },
  { customer_name: "Tomáš", product_purchased: "Notebook ProBook 14", stage: "tip_day3", channel: "email", brand_name: "TechHub" },
  { customer_name: "Eva", product_purchased: "Detox čaj 30-dňový", stage: "review_request", channel: "whatsapp", brand_name: "ZdraváVýživa" },
];

export default function AgentOsDemoCustomerOnboarding() {
  const [form, setForm] = useState({
    customer_name: "", product_purchased: "", stage: "welcome", channel: "email",
    brand_name: "", brand_voice: "warm", lang: "sk",
  });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });
  const [copied, setCopied] = useState(false);

  const loadExample = (i) => { setForm((f) => ({ ...f, ...EXAMPLES[i] })); setResult(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_purchased.trim()) return;
    setStatus({ loading: true, error: null }); setResult(null);
    try {
      const r = await axios.post(`${API}/agent-os/demo/customer-onboarding`, form);
      setResult(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setStatus({ loading: false, error: typeof d === "object" ? d : { message: d || "Skús to ešte raz." } });
      return;
    }
    setStatus({ loading: false, error: null });
  };

  const copyAll = async () => {
    if (!result?.output) return;
    const o = result.output;
    const txt = `${o.subject ? `Subject: ${o.subject}\n\n` : ""}${o.body}\n\nCTA: ${o.cta}`;
    try { await navigator.clipboard.writeText(txt); setCopied(true); } catch {}
    setTimeout(() => setCopied(false), 2000);
  };

  const Left = (
    <form onSubmit={submit} className="p-6 bg-black border border-gray-800" data-testid="customer-onboarding-form">
      <div className="flex gap-2 mb-5 flex-wrap">
        {EXAMPLES.map((_, i) => (
          <button key={i} type="button" onClick={() => loadExample(i)} data-testid={`customer-onboarding-example-${i}`}
            className="text-[10px] px-3 py-1 border border-emerald-700/40 text-emerald-300 hover:bg-emerald-900/20 font-bold uppercase tracking-widest">
            Príklad {i + 1}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Meno zákazníka"><input value={form.customer_name} maxLength={80} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
          placeholder="Lucia" data-testid="customer-onboarding-input-name"
          className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none" /></Field>
        <Field label="Značka"><input value={form.brand_name} maxLength={80} onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
          placeholder="WoodTime" data-testid="customer-onboarding-input-brand"
          className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none" /></Field>
      </div>
      <Field label="Zakúpený produkt"><input required value={form.product_purchased} maxLength={200}
        onChange={(e) => setForm({ ...form, product_purchased: e.target.value })}
        placeholder="napr. Sandalwood hodinky Aurora" data-testid="customer-onboarding-input-product"
        className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Stage"><select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}
          data-testid="customer-onboarding-input-stage"
          className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm">
          {STAGES.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select></Field>
        <Field label="Kanál"><select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}
          data-testid="customer-onboarding-input-channel"
          className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm">
          {CHANNELS.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select></Field>
      </div>
      <button type="submit" disabled={status.loading || !form.product_purchased}
        data-testid="customer-onboarding-submit"
        className="w-full mt-2 py-3 bg-emerald-400 hover:bg-emerald-300 disabled:bg-gray-700 disabled:text-gray-400 text-black font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2">
        {status.loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generujem…</> : <><Sparkles className="w-4 h-4" /> Generuj správu</>}
      </button>
    </form>
  );

  const Right = (
    <div className="p-6 bg-black border border-gray-800" data-testid="customer-onboarding-output">
      {!result && !status.loading && !status.error && <Placeholder Icon={UserPlus} text="Personalizovaná správa pre lifecycle drip kampaň" />}
      {status.loading && <Loading text="AI pripravuje personalizovanú správu…" />}
      {status.error && <ErrorBox err={status.error} />}
      {result?.output && (
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <span className="text-[10px] px-2 py-0.5 bg-emerald-900/40 text-emerald-300 font-bold uppercase tracking-widest border border-emerald-700/40">
                {result.output.channel}
              </span>
              <span className="text-[10px] px-2 py-0.5 bg-blue-900/40 text-blue-300 font-bold uppercase tracking-widest border border-blue-700/40">
                Day +{result.output.send_offset_days}
              </span>
            </div>
            <button onClick={copyAll} data-testid="customer-onboarding-copy"
              className="inline-flex items-center gap-1 text-[10px] px-2 py-1 border border-emerald-700/40 text-emerald-300 hover:bg-emerald-900/20 font-bold uppercase tracking-widest">
              {copied ? <><CheckCircle className="w-3 h-3" /> Kopírované</> : <><Copy className="w-3 h-3" /> Kopírovať</>}
            </button>
          </div>
          {result.output.subject && (
            <div><p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Subject</p>
              <p className="text-gray-200 font-semibold" data-testid="customer-onboarding-output-subject">{result.output.subject}</p></div>
          )}
          <div><p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Body</p>
            <p className="text-gray-200 whitespace-pre-wrap leading-relaxed" data-testid="customer-onboarding-output-body">{result.output.body}</p></div>
          <div className="pt-3 border-t border-gray-900"><p className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">CTA</p>
            <p className="text-gray-200 font-semibold">{result.output.cta}</p></div>
          <p className="text-[10px] text-gray-600 pt-3 border-t border-gray-900">Zostáva {result.remaining_today} demo runov. Latencia: {result.latency_ms}ms.</p>
        </div>
      )}
    </div>
  );

  return <AgentOsDemoShell testid="agent-os-demo-customer-onboarding" h1="Customer Onboarding Bot" lead="Lifecycle drip kampaň cez email/WhatsApp/SMS — personalizovaná za 5s." left={Left} right={Right} />;
}

const Field = ({ label, children }) => (<label className="block mb-3"><span className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">{label}</span>{children}</label>);
const Placeholder = ({ Icon, text }) => (<div className="text-center py-10"><Icon className="w-10 h-10 text-gray-700 mx-auto mb-3" /><p className="text-sm text-gray-500">{text}</p></div>);
const Loading = ({ text }) => (<div className="text-center py-10"><Loader2 className="w-10 h-10 text-emerald-400 animate-spin mx-auto mb-3" /><p className="text-sm text-gray-400">{text}</p></div>);
const ErrorBox = ({ err }) => (<div className="p-4 border border-red-900/60 bg-red-950/30 text-sm text-red-300" data-testid="customer-onboarding-error">{err.message || "Skús to znova."}</div>);
